import { useState } from "react";
import { permissionsToLabelString } from "../../constants/delegationPermissions";
import { AvailableLawyerModel } from "../../interfaces/delegation";
import { useSwitchDelegation } from "../../hooks/delegation/useSwitchDelegation";
import { ApiRequestError } from "../../api/util";
import { Button } from "../Button";
import Spinner from "../graphics/Spinner";

interface DelegationLawyerSelectProps {
  delegateName?: string | null;
  lawyers: AvailableLawyerModel[];
}

const getErrorMessage = (error: unknown) => {
  if (error instanceof ApiRequestError) return error.message;
  if (error instanceof Error) return error.message;
  return "Kunne ikke logge ind som advokat. Prøv igen.";
};

const DelegationLawyerSelect = ({
  delegateName,
  lawyers,
}: DelegationLawyerSelectProps) => {
  const switchMutation = useSwitchDelegation();
  const [error, setError] = useState<string | null>(null);
  const [activeDelegationId, setActiveDelegationId] = useState<string | null>(
    null,
  );

  const handleSelect = async (delegationId: string) => {
    setError(null);
    setActiveDelegationId(delegationId);
    try {
      await switchMutation.mutateAsync({ delegationId });
    } catch (err) {
      setActiveDelegationId(null);
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="bg-white pb-8 pt-8 mt-n8">
      <div className="container-xxl">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8">
            <h1 className="display-1 mb-3" style={{ fontSize: "2.75rem" }}>
              Vælg advokat
            </h1>
            <p className="fw-light mb-5">
              {delegateName ? (
                <>
                  Hej {delegateName}. Du har fuldmagt til at handle på vegne af
                  følgende advokater. Vælg hvem du vil logge ind som.
                </>
              ) : (
                <>
                  Du har fuldmagt til at handle på vegne af følgende advokater.
                  Vælg hvem du vil logge ind som.
                </>
              )}
            </p>

            {error ? (
              <div
                className="alert alert-danger d-flex align-items-start justify-content-between gap-3 mb-4"
                role="alert"
              >
                <span>{error}</span>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Luk fejlmeddelelse"
                  onClick={() => setError(null)}
                />
              </div>
            ) : null}

            <div className="vstack gap-3">
              {lawyers.map((lawyer) => {
                const isLoading =
                  switchMutation.isPending &&
                  activeDelegationId === lawyer.delegationId;

                return (
                  <div
                    key={lawyer.delegationId}
                    className="card border shadow-sm"
                  >
                    <div className="card-body d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                      <div>
                        <h2 className="h5 fw-semibold mb-1">
                          {lawyer.lawyerName}
                        </h2>
                        <p className="text-muted mb-0 small">
                          Rettigheder:{" "}
                          {permissionsToLabelString(lawyer.permissions)}
                        </p>
                      </div>
                      <Button
                        id={`delegation-select-${lawyer.delegationId}`}
                        type="button"
                        color="primary"
                        className="flex-shrink-0"
                        disabled={switchMutation.isPending}
                        onClick={() => handleSelect(lawyer.delegationId)}
                        aria-label={`Log ind som ${lawyer.lawyerName}`}
                      >
                        {isLoading ? (
                          <span className="d-inline-flex align-items-center gap-2">
                            <Spinner inline size={16} label="" />
                            Logger ind...
                          </span>
                        ) : (
                          `Log ind som ${lawyer.lawyerName}`
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DelegationLawyerSelect;
